import React, { useState } from 'react';
import { Order, Organization } from 'constants/interface';
import { Form } from 'antd';
import {
  Button,
  Input,
  Popconfirm,
  Switch,
} from 'UI';
import SectionBasePage from 'components/SectionBasePage';
import {
  OrderOtherStatusEnum,
  OrderProductionStatusEnum,
  OrderProposalStatusEnum,
  OrderStatusDisplayMapping,
  OrderStumpTypeEnum,
  OrderTypeEnum,
} from 'constants/order';
import { createAlertNotification } from 'infra/notification';
import { AlertNotificationType, setNotificationAction } from 'state/slices/notification';
import { useDispatch } from 'hooks/store';
import { CreateOrderData, CreateOrderItemData } from 'api/order';
import { createOrderAction, setOrderStatusAction, updateOrderAction } from 'state/slices/order';
import { unwrapResult } from '@reduxjs/toolkit';
import { useBreadcrumbNavigation } from 'hooks/breadcrumb';

import {
  ClientInfo,
  ClientInfoFormData,
  OrderInfo,
  OrderInfoFormData,
  OrderItems,
  AdvanceInfo,
  Extra,
  RegularOrderItemsFormData,
  GraftingOrderItemsFormData,
  AdvanceInfoFormData,
} from './components';

import {
  MainContainer,
  HorizontalContainer,
  OrderOptionalInfoFormContainer,
  NotesInline,
  BaseFormItem,
  OrderStatusFormContainer,
  CancelContainer,
  StatusTag,
} from './style';

interface OrderStatusFormData {
  isConfirmed: boolean
}

interface OrderOptionalInfoFormData {
  notes?: string
}

interface EditOrderProps {
  selectedOrg: Organization
  activeOrder?: Order
  isCopy?: boolean
}

const EditOrder = ({
  selectedOrg, // TODO: use context org
  activeOrder,
  isCopy = false,
}: EditOrderProps): JSX.Element => {
  const dispatch = useDispatch();
  const navigator = useBreadcrumbNavigation();

  const [orderStatusForm] = Form.useForm<OrderStatusFormData>();
  const [clientInfoForm] = Form.useForm<ClientInfoFormData>();
  const [orderInfoForm] = Form.useForm<OrderInfoFormData>();
  const [regularOrderItemsForm] = Form.useForm<RegularOrderItemsFormData>();
  const [graftingOrderItemsForm] = Form.useForm<GraftingOrderItemsFormData>();
  const [orderOptionalInfoForm] = Form.useForm<OrderOptionalInfoFormData>();
  const [advanceInfoForm] = Form.useForm<AdvanceInfoFormData>();

  const [isLoading, setLoading] = useState(false);
  const [isCancelSubmitting, setCancelSubmitting] = useState(false);
  const [isProductionSubmitting, setProductionSubmitting] = useState(false);

  const isActiveOrderConfirmed = (
    activeOrder
    && activeOrder?.status !== OrderProposalStatusEnum.SCHEDULED
  );

  const [
    currentOrderType,
    setCurrentOrderType,
  ] = useState(activeOrder?.type || OrderTypeEnum.REGULAR);

  const onSave = async () => {
    try {
      await Promise.all<
      OrderStatusFormData,
      ClientInfoFormData,
      OrderInfoFormData,
      RegularOrderItemsFormData | GraftingOrderItemsFormData,
      OrderOptionalInfoFormData,
      AdvanceInfoFormData
      >([
        orderStatusForm.validateFields(),
        clientInfoForm.validateFields(),
        orderInfoForm.validateFields(),
        currentOrderType === OrderTypeEnum.REGULAR
          ? regularOrderItemsForm.validateFields()
          : graftingOrderItemsForm.validateFields(),
        orderOptionalInfoForm.validateFields(),
        advanceInfoForm.validateFields(),
      ]);
    } catch (e) {
      const errorAlert = createAlertNotification(
        'Verifique os campos com erro.',
        AlertNotificationType.Error,
      );
      dispatch(setNotificationAction(errorAlert));
      return;
    }

    const orderStatusFormData = orderStatusForm.getFieldsValue();
    const clientInfoFormData = clientInfoForm.getFieldsValue();
    const orderInfoFormData = orderInfoForm.getFieldsValue();
    const regularOrderItemsFormData = regularOrderItemsForm.getFieldsValue();
    const graftingOrderItemsFormData = graftingOrderItemsForm.getFieldsValue();
    const orderOptionalInfoFormData = orderOptionalInfoForm.getFieldsValue();
    const advanceInfoFormData = advanceInfoForm.getFieldsValue();

    const normalizedItemsData:
    CreateOrderItemData[] = orderInfoFormData.type === OrderTypeEnum.REGULAR
      ? regularOrderItemsFormData
        .items
        .map((regularFormItemData) => {
          const normalized: CreateOrderItemData = {
            unit: regularFormItemData.unit,
            orderQuantity: parseInt(regularFormItemData.orderQuantity, 10),
            pruning: regularFormItemData.pruning,
            stumpType: OrderStumpTypeEnum.DISCARD,
            plannedSeedDate: regularFormItemData.plannedSeedDate.toISOString(),
            plannedDeliveryDate: regularFormItemData.plannedDeliveryDate.toISOString(),
            unitPrice: parseFloat(regularFormItemData.unitPrice) * 100,
            mainProduct: {
              product: regularFormItemData.product,
              tray: regularFormItemData.tray,
              seedSource: regularFormItemData.seedSource,
            },
          };

          return normalized;
        })
      : graftingOrderItemsFormData
        .items
        .map((graftingFormItemData) => {
          const normalized: CreateOrderItemData = {
            unit: graftingFormItemData.unit,
            orderQuantity: parseInt(graftingFormItemData.orderQuantity, 10),
            pruning: graftingFormItemData.pruning,
            stumpType: graftingFormItemData.stumpType,
            stumpQuantity: parseInt(graftingFormItemData.stumpQuantity || '0', 10) || undefined,
            plannedSeedDate: graftingFormItemData.plannedSeedDate.toISOString(),
            plannedDeliveryDate: graftingFormItemData.plannedDeliveryDate.toISOString(),
            unitPrice: parseFloat(graftingFormItemData.unitPrice) * 100,
            stumpUnitPrice: (parseFloat(graftingFormItemData.stumpUnitPrice || '0') * 100) || undefined,
            mainProduct: {
              product: graftingFormItemData.scionProduct,
              tray: graftingFormItemData.scionTray,
              seedSource: graftingFormItemData.scionSeedSource,
            },
            stockProduct: {
              product: graftingFormItemData.stockProduct,
              tray: graftingFormItemData.stockTray,
              seedSource: graftingFormItemData.stockSeedSource,
            },
          };

          return normalized;
        });

    const createOrderPayload: CreateOrderData = {
      isConfirmed: orderStatusFormData.isConfirmed,

      client: clientInfoFormData.client,
      location: clientInfoFormData.location,

      type: orderInfoFormData.type,
      organization: orderInfoFormData.organization,
      deliveryType: orderInfoFormData.deliveryType,

      items: normalizedItemsData,

      notes: orderOptionalInfoFormData.notes,

      advancePaymentDate: advanceInfoFormData.advancePaymentDate?.toISOString(),
      advancePaymentMethod: advanceInfoFormData.advancePaymentMethod,
      advancePaymentPrice: (parseFloat(advanceInfoFormData.advancePaymentPrice) * 100) || undefined,
      advancePaymentResponsible: advanceInfoFormData.advancePaymentResponsible,
      advancePaymentConfirmed: advanceInfoFormData.advancePaymentConfirmed,
    };

    try {
      setLoading(true);
      // const result = await dispatch(updateOrderAction({
      //   id: activeOrder.id,
      //   data: createOrderPayload,
      // }));
      if (activeOrder && isCopy === false) {
        const updateResult = await dispatch(updateOrderAction({
          id: activeOrder.id,
          data: createOrderPayload,
        }));
        unwrapResult(updateResult);
      } else {
        const createResult = await dispatch(createOrderAction(createOrderPayload));
        unwrapResult(createResult);
      }
      // const unwrappedResultOrder = unwrapResult(result);
      // unwrapResult(result);
      const successAlert = createAlertNotification(`
      ${createOrderPayload.isConfirmed ? 'Pedido' : 'Agendamento'} ${activeOrder ? 'atualizado' : 'criado'} com sucesso.
      `, AlertNotificationType.Success);
      dispatch(setNotificationAction(successAlert));
      setLoading(false);
      // TODO: maybe put base URL as props to this component?
      // navigator.replace([{
      //   label: `Pedido Nº ${unwrappedResultOrder.seq}`,
      //   url: `${ROUTES.orders.root}/${unwrappedResultOrder.id}`,
      // }], 1);
      navigator.toPrevious();
    } catch (err) {
      const errorAlert = createAlertNotification(
        'Erro interno ao tentar criar pedido. Tente novamente.',
        AlertNotificationType.Error,
      );
      dispatch(setNotificationAction(errorAlert));
      setLoading(false);
    }
  };

  const onCancelOrder = async () => {
    if (!activeOrder) {
      return;
    }
    try {
      setLoading(true);
      setCancelSubmitting(true);
      await dispatch(setOrderStatusAction({
        orderId: activeOrder.id,
        status: OrderOtherStatusEnum.CANCELED,
      }));
      setCancelSubmitting(false);
      setLoading(false);
      navigator.toPrevious();
    } catch (err) {
      const errorAlert = createAlertNotification(
        'Erro interno ao cancelar pedido. Tente novamente.',
        AlertNotificationType.Error,
      );
      dispatch(setNotificationAction(errorAlert));
      setCancelSubmitting(false);
      setLoading(false);
    }
  };

  const onSendOrderToProduction = async () => {
    if (!activeOrder) {
      return;
    }
    try {
      setLoading(true);
      setProductionSubmitting(true);
      await dispatch(setOrderStatusAction({
        orderId: activeOrder.id,
        status: OrderProductionStatusEnum.PRODUCTION,
      }));
      setProductionSubmitting(false);
      setLoading(false);
      navigator.toPrevious();
    } catch (err) {
      const errorAlert = createAlertNotification(
        'Erro interno ao enviar pedido para semeio. Tente novamente.',
        AlertNotificationType.Error,
      );
      dispatch(setNotificationAction(errorAlert));
      setProductionSubmitting(false);
      setLoading(false);
    }
  };

  const isActiveOrderCanceled = activeOrder?.status === OrderOtherStatusEnum.CANCELED;

  return (
    <SectionBasePage
      extra={(
        <Extra
          onSave={onSave}
          onSendToProduction={
            activeOrder
            && !isCopy
            && !isActiveOrderCanceled
            && activeOrder.status === OrderProposalStatusEnum.CONFIRMED
              ? onSendOrderToProduction
              : undefined
            }
          isLoading={isLoading || isProductionSubmitting}
          isDisabled={isActiveOrderCanceled}
        />
      )}
      titleAppend={
        activeOrder && !isCopy
          ? (
            <StatusTag color={OrderStatusDisplayMapping[activeOrder.status].color}>
              {OrderStatusDisplayMapping[activeOrder.status].name}
            </StatusTag>
          )
          : undefined
        }
    >
      <MainContainer>
        <OrderStatusFormContainer
          form={orderStatusForm}
          layout="vertical"
          initialValues={{
            isConfirmed: (isCopy === true)
              ? false
              : (isActiveOrderConfirmed || false),
          }}
        >
          <HorizontalContainer>
            <BaseFormItem
              label="Confirmado"
              name="isConfirmed"
              valuePropName="checked"
            >
              <Switch
                disabled={
                  (isCopy === true)
                    ? false
                    : isActiveOrderConfirmed || isLoading || isActiveOrderCanceled
                }
              />
            </BaseFormItem>
            {
              activeOrder && !isCopy && (
                <>
                  <BaseFormItem
                    label="Data do pedido"
                  >
                    <Input
                      disabled
                      value={(() => {
                        const date = new Date(activeOrder.createdAt);
                        const formattedDate = date.toLocaleDateString('pt-br');
                        return formattedDate;
                      })()}
                    />
                  </BaseFormItem>
                  <BaseFormItem
                    label="Última atualização"
                  >
                    <Input disabled value={activeOrder.updatedBy.name} />
                  </BaseFormItem>
                </>
              )
            }
          </HorizontalContainer>
        </OrderStatusFormContainer>
        <ClientInfo
          formHandler={clientInfoForm}
          initialOrder={activeOrder}
          isLoading={isLoading}
          isCopy={isCopy}
        />
        <OrderInfo
          formHandler={orderInfoForm}
          initialOrder={activeOrder}
          onOrderTypeChange={(type) => setCurrentOrderType(type)}
          isLoading={isLoading}
        />
        <OrderItems
          orderType={currentOrderType}
          regularFormHandler={regularOrderItemsForm}
          graftingFormHandler={graftingOrderItemsForm}
          isLoading={isLoading}
          initialOrder={activeOrder}
        />
        <OrderOptionalInfoFormContainer
          form={orderOptionalInfoForm}
          layout="vertical"
          initialValues={{
            notes: activeOrder?.notes || undefined,
          }}
        >
          <NotesInline
            label="Observações do pedido"
            name="notes"
          >
            <Input.TextArea rows={1} disabled={isLoading || isActiveOrderCanceled} />
          </NotesInline>
        </OrderOptionalInfoFormContainer>

        <AdvanceInfo
          formHandler={advanceInfoForm}
          isLoading={isLoading}
          initialOrder={activeOrder}
          isCopy={isCopy}
        />

        {activeOrder && !isCopy && !isActiveOrderCanceled && (
          <CancelContainer>
            <Popconfirm
              trigger="click"
              onConfirm={onCancelOrder}
              title="Deseja cancelar o pedido?"
              okText="Sim"
              cancelText="Não"
            >
              <Button
                danger
                disabled={isLoading}
                loading={isCancelSubmitting}
              >
                Cancelar pedido
              </Button>
            </Popconfirm>
          </CancelContainer>
        )}
      </MainContainer>
    </SectionBasePage>
  );
};

EditOrder.defaultProps = {
  activeOrder: undefined,
};

export default EditOrder;
